import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Square, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const MAX = 20;

export const VoiceRecorder = ({ value, onChange, endpoint = "/voice-notes", extra = {}, label = `Voice note (optional · up to ${MAX}s)` }) => {
  const [rec, setRec] = useState(false);
  const [secs, setSecs] = useState(0);
  const [blobUrl, setBlobUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const mr = useRef(null), chunks = useRef([]), timer = useRef(null), startedAt = useRef(0);
  useEffect(() => () => { clearInterval(timer.current); mr.current?.stream?.getTracks().forEach((t) => t.stop()); }, []);

  const upload = async (blob, duration) => {
    setUploading(true);
    const fd = new FormData();
    fd.append("file", blob, `note.${blob.type.includes("mp4") ? "m4a" : "webm"}`);
    fd.append("durationSec", String(duration));
    Object.entries(extra).forEach(([k, v]) => v != null && fd.append(k, String(v)));
    try { const r = await api.post(endpoint, fd); onChange(r.data); }
    catch (e) { toast.error(errMsg(e)); setBlobUrl(null); } finally { setUploading(false); }
  };
  const stop = () => { if (mr.current?.state === "recording") mr.current.stop(); };
  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) return toast.error("Recording isn't supported in this browser");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"].find((m) => MediaRecorder.isTypeSupported(m));
      const r = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunks.current = [];
      r.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
      r.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(timer.current); setRec(false);
        const duration = Math.min(MAX, Math.round((Date.now() - startedAt.current) / 100) / 10);
        const blob = new Blob(chunks.current, { type: (r.mimeType || "audio/webm").split(";")[0] });
        setBlobUrl(URL.createObjectURL(blob)); setSecs(duration);
        upload(blob, duration);
      };
      mr.current = r; startedAt.current = Date.now(); setSecs(0); setRec(true);
      r.start(250);
      timer.current = setInterval(() => { const s = (Date.now() - startedAt.current) / 1000; setSecs(Math.min(MAX, Math.round(s * 10) / 10)); if (s >= MAX) stop(); }, 100);
    } catch { toast.error("Microphone access was blocked"); }
  };
  const clear = () => { setBlobUrl(null); setSecs(0); onChange(null); };

  return (
    <div data-testid="voice-recorder">
      <label>{label}</label>
      {!blobUrl && !value && (
        <div className="rec">
          <button type="button" className={`btn sm ${rec ? "danger" : "g"}`} onClick={rec ? stop : start} data-testid={rec ? "voice-stop-button" : "voice-record-button"}>
            {rec ? <><Square size={12} style={{ verticalAlign: -1, marginRight: 5 }} />Stop</> : <><Mic size={12} style={{ verticalAlign: -1, marginRight: 5 }} />Record</>}
          </button>
          {rec && <><span className="dotrec" /><span className="num ss" data-testid="voice-timer">{secs.toFixed(1)}s / {MAX}s</span></>}
        </div>
      )}
      {(blobUrl || value) && (
        <div className="rec" style={{ flexWrap: "wrap" }}>
          <audio className="vn" controls src={blobUrl || value?.url} data-testid="voice-preview" />
          <span className="ss num" data-testid="voice-status">{uploading ? "Uploading & transcribing…" : value ? `Attached · ${value.durationSec}s` : `${secs}s`}</span>
          {value?.transcript && <div className="ss" style={{ width: "100%" }} data-testid="voice-transcript">📝 “{value.transcript}”</div>}
          <button type="button" className="btn sm g" onClick={clear} disabled={uploading} aria-label="Remove voice note" data-testid="voice-remove-button"><Trash2 size={12} /></button>
        </div>
      )}
    </div>
  );
};

export const VoicePlayer = ({ id, testId, transcript }) => {
  const [tr, setTr] = useState(transcript ?? undefined);
  const [open, setOpen] = useState(false);
  useEffect(() => { if (id && transcript === undefined) api.get(`/voice-notes/${id}/transcript`).then((r) => setTr(r.data.transcript)).catch(() => setTr(null)); }, [id, transcript]);
  if (!id) return null;
  return (
    <div data-testid={testId ? `${testId}-wrap` : undefined}>
      <audio className="vn" controls preload="none" src={`${process.env.REACT_APP_BACKEND_URL}/api/public/voice/${id}`} data-testid={testId || `voice-player-${id}`} />
      {tr && (
        <div className="transcript" data-testid={`${testId || `voice-player-${id}`}-transcript`}>
          <button type="button" className="linkbtn" onClick={() => setOpen(!open)} data-testid={`${testId || `voice-player-${id}`}-transcript-toggle`}>{open ? "Hide transcript" : "Transcript"}</button>
          {open && <div className="ss" style={{ marginTop: 2 }}>“{tr}”</div>}
        </div>
      )}
    </div>
  );
};
